<template>
    <!-- 左侧固定半透明半圆触发器 -->
    <div class="xb-refresh-trigger" @click="togglePanel"></div>

    <!-- 刷新按钮面板 -->
    <transition name="fade-slide">
        <div v-if="visible" class="xb-refresh-panel" @click.stop @mouseenter="clearHideTimer" @mousemove="resetHideTimer" @mouseleave="resetHideTimer">
            <div
                class="refresh-button border"
                :class="buttonClass"
                :style="{
                    borderColor: `var(${borderVarName}, ${borderColor})`,
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    backgroundColor: `var(${bgVarName}, ${backgroundColor})`,
                    color: `var(${textVarName}, ${textColor})`,
                }"
                @click="handleRefresh"
            >
                <svg t="1757642264529"
                    class="icon refresh-icon"
                    viewBox="0 0 1024 1024"
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                    p-id="14045"
                    width="24"
                    height="24"
                    :style="{ color: `var(${iconVarName}, ${textColor})` }">
                        <path d="M958.111 59.526l-107.462 113.118c-89.082-91.908-212.098-147.054-347.839-147.054-270.069 0-490.652 220.582-490.652 489.237 0 270.069 220.582 489.237 490.652 489.237 203.613 0 419.953-111.705 466.614-339.355 4.242-21.21-12.725-32.522-29.694-32.522h-100.393c-21.21 0-39.591 19.795-41.007 33.935 0 2.829 0 4.242-1.414 4.242-45.248 132.914-148.468 165.437-294.108 165.437-176.747 0-320.975-144.226-320.975-320.975s144.226-319.559 320.975-319.559c89.082 0 168.263 36.763 229.065 96.151l-139.984 138.57c-1.414 0-1.414 1.414-2.829 1.414-28.278 31.108-16.967 52.317 15.553 52.317h353.495c31.108 0 50.903-21.21 50.903-52.317v-359.152c-1.414-26.866-19.795-36.763-50.903-12.725z"
                        fill="currentColor"
                        p-id="14046">
                        </path>
                </svg>
            </div>
        </div>
    </transition>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';

interface Props {
    left?: number;
    bottom?: number;
    backgroundColor?: string;
    borderColor?: string;
    textColor?: string;
    buttonClass?: string;
    bgVarName?: string;
    borderVarName?: string;
    textVarName?: string;
    iconVarName?: string;
}

const props = withDefaults(defineProps<Props>(), {
    left: 16,  // 距离左边的像素值
    bottom: 100, // 距离底部的像素值，避免被底部导航栏遮挡
    backgroundColor: 'rgba(0,0,0)',
    borderColor: 'var(--refresh-button-border, #16a34a)',
    textColor: '#ffffff',
    buttonClass: '',
    bgVarName: '--refresh-button-bg',
    borderVarName: '--refresh-button-border',
    textVarName: '--refresh-button-text',
    iconVarName: '--refresh-button-icon',
});

// 控制面板显隐
const visible = ref(false);
// 定时器引用
let hideTimer: NodeJS.Timeout | null = null;
// 5秒无操作自动隐藏
const HIDE_DELAY = 5000;

onMounted(() => {
    // 初始关闭
    visible.value = false;
});

onUnmounted(() => {
    // 清理定时器
    clearHideTimer();
});

// 重置隐藏定时器（与组件自身交互时重置倒计时）
function resetHideTimer() {
    clearHideTimer();
    if (visible.value) {
        hideTimer = setTimeout(() => {
            visible.value = false;
        }, HIDE_DELAY);
    }
}

// 清理隐藏定时器
function clearHideTimer() {
    if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
    }
}

function handleRefresh() {
    window.location.reload();
}

function togglePanel() {
    visible.value = !visible.value;
    // 当显示面板时，开始5秒倒计时
    if (visible.value) {
        resetHideTimer();
    } else {
        clearHideTimer();
    }
}
</script>

<style scoped>
/* 左侧固定半透明半圆触发器 */
.xb-refresh-trigger {
    position: fixed;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 20px; /* 半圆的半径 - 减小 */
    height: 40px; /* 半圆的直径 - 减小 */
    background: rgba(0, 0, 0, 0.15); /* 降低透明度 */
    border-top-right-radius: 25px; /* 右侧为圆弧 - 调整 */
    border-bottom-right-radius: 25px;
    border-top-left-radius: 0; /* 左侧贴边 */
    border-bottom-left-radius: 0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08); /* 降低阴影透明度 */
    z-index: 1000;
}

/* 中部白色半圆（增强可见性，类似虚拟键手柄） */
.xb-refresh-trigger::after {
    content: '';
    position: absolute;
    top: 50%;
    left: -10px; /* 一半在屏幕外，只显示内侧半圆 - 调整位置 */
    transform: translateY(-50%);
    width: 20px; /* 减小尺寸 */
    height: 20px;
    background: rgba(255, 255, 255, 0.5); /* 降低透明度 */
    border-radius: 50%;
    border: 1.5px solid rgba(0, 0, 0, 0.7); /* 减小边框粗细和透明度 */
}

/* 刷新按钮面板（触发器右侧） */
.xb-refresh-panel {
    position: fixed;
    left: 20px; /* 调整位置，与触发器尺寸匹配 */
    top: 48%;
    transform: translateY(-50%);
    z-index: 1000;
}

.refresh-button {
    width: 45px;
    height: 45px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    user-select: none;
    opacity: 0.8; /* 提高不透明度，让按钮更亮 */
}

.refresh-button:hover {
    opacity: 1; /* hover时完全不透明 */
    transform: scale(1.1); /* 增大缩放效果 */
}

.refresh-button:active {
    transform: scale(0.95); /* 调整按下效果 */
    opacity: 0.9; /* 按下时稍微透明 */
}

/* 简单的淡入滑动过渡 */
.fade-slide-enter-active,
.fade-slide-leave-active {
    transition: all 0.15s ease;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
    opacity: 0;
    transform: translateY(-50%) translateX(-8px);
}
</style>
